/**
 * Configuration for navigation links that point to the new AYANA website.
 *
 * Pages are being migrated from this rewards site to the new Astro-built
 * ayana.com site. This module maps old-site nav paths to new-site URLs
 * and handles environment + locale differences.
 *
 * To migrate a new page: add one entry to MIGRATED_PAGES below.
 * Login/register are pending — add them here when decided.
 *
 * Feature flag: GATSBY_USE_NEW_SITE_NAV
 *   - "true"  → nav points to the new site (enabled)
 *   - anything else → nav keeps pointing to the old site's own pages (disabled)
 *
 * This lets the patch merge safely without depending on the new site being
 * ready. Flip the flag to "true" in Vercel when the new site is live.
 */

const USE_NEW_SITE_NAV = process.env.GATSBY_USE_NEW_SITE_NAV === "true";

/**
 * Map of old-site path suffixes → new-site paths.
 * Key = the slug appended to home_link in nav (e.g. "promotion").
 * Value = the path on the new site (without locale prefix).
 */
const MIGRATED_PAGES = {
  "": "/rewards/",
  "promotion": "/rewards/promotion/",
  "member-benefits": "/rewards/member-benefits/",
  "partner": "/rewards/partners/",
  "reward": "/rewards/redeem/",
  "faq": "/rewards/faqs/",
  "terms-and-conditions-8": "/rewards/terms-and-conditions/",
};

/**
 * Privacy policy on the new site is a hash anchor on the T&C page
 * (mirrors the old site's "terms-and-conditions-8#data-privacy" pattern).
 */
const PRIVACY_NEW_PATH = "/rewards/terms-and-conditions/#data-privacy";

/**
 * Resolve the base URL of the new site.
 *
 * Priority:
 * 1. GATSBY_NEW_SITE_URL env var (baked in at build time — set per environment in Vercel)
 * 2. Runtime hostname detection (rewards.ayana.com → production)
 * 3. Default to staging
 */
function getNewSiteBaseUrl() {
  if (process.env.GATSBY_NEW_SITE_URL) {
    return process.env.GATSBY_NEW_SITE_URL.replace(/\/$/, "");
  }
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "rewards.ayana.com"
  ) {
    return "https://ayana.com";
  }
  return "https://site-dev.ayana.com";
}

/**
 * Map a langkey to the new site's locale URL prefix.
 * English has no prefix; others get /{langkey}.
 */
function getLocalePrefix(langkey) {
  if (!langkey || langkey === "en") return "";
  return "/" + langkey;
}

/**
 * Build the URL for a migrated nav link.
 * - Flag ON: returns a full URL to the new site.
 * - Flag OFF: returns the old-site relative path (preserves pre-patch behavior).
 *
 * @param {string} oldPath - Old site path suffix (e.g. "promotion", "faq", "" for home)
 * @param {string} langkey - Current locale ("en", "ja", "zh")
 * @returns {string}
 */
function getNewSiteUrl(oldPath, langkey) {
  if (!USE_NEW_SITE_NAV) {
    const prefix = !langkey || langkey === "en" ? "" : "/" + langkey;
    if (oldPath === "") return prefix + "/";
    return prefix + "/" + oldPath;
  }
  const newPath = MIGRATED_PAGES[oldPath];
  if (newPath === undefined) return null;
  return getNewSiteBaseUrl() + getLocalePrefix(langkey) + newPath;
}

/**
 * Build the URL for the privacy policy link.
 * - Flag ON: new site T&C page with #data-privacy anchor.
 * - Flag OFF: old site T&C page with #data-privacy anchor.
 */
function getPrivacyUrl(langkey) {
  if (!USE_NEW_SITE_NAV) {
    const prefix = !langkey || langkey === "en" ? "" : "/" + langkey;
    return prefix + "/terms-and-conditions-8#data-privacy";
  }
  return getNewSiteBaseUrl() + getLocalePrefix(langkey) + PRIVACY_NEW_PATH;
}

export {
  MIGRATED_PAGES,
  getNewSiteBaseUrl,
  getLocalePrefix,
  getNewSiteUrl,
  getPrivacyUrl,
};
